import { create } from "zustand";
import { DealTransformed } from "@/lib/schema/deal-schema";
import { dealsApi } from "@/api/deals-api";
import { buildTearsheetsAll, transformDealData } from "@/services/deals-service";
import { setPostHogGroup } from "@/lib/posthog";

interface CurrentDealStore {
  currentDeal: DealTransformed | null;
  isLoading: boolean;
  error: string | null;

  fetchDeal: (workspaceId: string, dealId: string) => Promise<void>;
  updateDeal: (updates: Partial<DealTransformed>) => void;
  clearDeal: () => void;
  setDeal: (deal: DealTransformed) => void;
}

export const useCurrentDealStore = create<CurrentDealStore>()((set) => ({
  currentDeal: null,
  isLoading: false,
  error: null,

  fetchDeal: async (workspaceId: string, dealId: string) => {
    set({ isLoading: true, error: null });

    try {
      console.log("=== fetchDeal called ===");
      console.log("workspaceId:", workspaceId);
      console.log("dealId:", dealId);
      const rawDeal = await dealsApi.getDealById(workspaceId, dealId);
      console.log("Raw deal from API:", rawDeal);
      console.log("rawDeal.ai_tracked:", rawDeal.ai_tracked);
      const transformedDeal = transformDealData(rawDeal);
      console.log("Transformed deal:", transformedDeal);
      console.log("transformedDeal.aiTracked:", transformedDeal.aiTracked);

      // Set PostHog group for company analytics
      setPostHogGroup("company", transformedDeal.id, {
        name: transformedDeal.overview.name,
      });

      // The deal-fetch projection omits the source-data reports
      // (website_summary_v2, linkedInInfo) — they're hydrated separately via
      // /get_reports and only ever live in this store. On a refetch of the
      // SAME deal (e.g. questionnaire save → fetchDeal) the fresh projection
      // carries empty arrays for them and the separate hydration won't re-run,
      // so a plain replace would strand the analysis cards on "Run". Keep the
      // already-hydrated values when the fresh fetch has none; a deal switch
      // (different id) starts clean.
      set((state) => {
        const prev =
          state.currentDeal?.id === transformedDeal.id
            ? state.currentDeal
            : null;
        const currentDeal = prev
          ? {
              ...transformedDeal,
              aiGenerations: {
                ...transformedDeal.aiGenerations,
                website_summary_v2:
                  transformedDeal.aiGenerations.website_summary_v2.length > 0
                    ? transformedDeal.aiGenerations.website_summary_v2
                    : prev.aiGenerations.website_summary_v2,
                linkedInInfo:
                  (transformedDeal.aiGenerations.linkedInInfo?.length ?? 0) > 0
                    ? transformedDeal.aiGenerations.linkedInInfo
                    : prev.aiGenerations.linkedInInfo,
              },
            }
          : transformedDeal;
        return { currentDeal, isLoading: false, error: null };
      });
      console.log("Deal set in store, aiTracked:", transformedDeal.aiTracked);
    } catch (error) {
      console.error("Failed to fetch deal:", error);
      set({
        currentDeal: null,
        isLoading: false,
        error: error instanceof Error ? error.message : "Failed to fetch deal",
      });
    }
  },

  updateDeal: (updates: Partial<DealTransformed>) => {
    set((state) => {
      if (!state.currentDeal) return state;

      // Debug: Log the update
      console.log("=== updateDeal called ===");
      console.log("Current aiTracked:", state.currentDeal.aiTracked);
      console.log("Updates object:", updates);
      console.log("Updates.aiTracked:", (updates as any).aiTracked);
      
      const newDeal = {
        ...state.currentDeal,
        ...updates,
      };
      
      // Ensure aiTracked is preserved if not explicitly updated
      if (!('aiTracked' in updates)) {
        newDeal.aiTracked = state.currentDeal.aiTracked;
        console.log("Preserving aiTracked:", newDeal.aiTracked);
      } else {
        console.log("aiTracked explicitly updated to:", newDeal.aiTracked);
      }

      console.log("Final deal.aiTracked:", newDeal.aiTracked);

      // Merge aiGenerations onto the FRESH store value, not the partial the
      // caller handed us. Callers build `updates.aiGenerations` by spreading
      // `currentDeal.aiGenerations` from a closure that can be stale — and
      // some keys (website_summary_v2, linkedInInfo) aren't in the deal-fetch
      // projection, so they only ever live here via a separate hydration. A
      // stale spread omits those keys, and replacing wholesale would drop
      // them (stranding the analysis cards on "Run"). Merging fresh-first
      // keeps any key the caller didn't explicitly set, while explicit
      // changes (including deletes that pass a filtered array) still win.
      // `tearsheets_all` is then recomputed from the merged `tearsheets_v2`:
      // callers mutate that source array but never the derived sorted union,
      // so the panel toolbar dropdown would otherwise stay stale.
      if ('aiGenerations' in updates && updates.aiGenerations) {
        const mergedAiGenerations = {
          ...state.currentDeal.aiGenerations,
          ...updates.aiGenerations,
        };
        newDeal.aiGenerations = {
          ...mergedAiGenerations,
          tearsheets_all: buildTearsheetsAll(
            mergedAiGenerations.tearsheets_v2,
          ),
        };
      }

      return {
        currentDeal: newDeal,
      };
    });
  },

  clearDeal: () => {
    set({
      currentDeal: null,
      isLoading: false,
      error: null,
    });
  },

  setDeal: (deal: DealTransformed) => {
    // Set PostHog group for company analytics
    setPostHogGroup("company", deal.id, {
      name: deal.overview.name,
    });

    set({
      currentDeal: deal,
      isLoading: false,
      error: null,
    });
  },
}));
